import { FuelBarcode } from "./fuelBarcodes.types";

/**
 * Built-in defaults for "Import defaults":
 *   - barcode      → V1 (Rotada-style)
 *   - productClass → V2 (E100 Class)
 *   - serviceType  → V3 (Paslaugos rūšis)
 */
export const DEFAULT_FUEL_BARCODE_VALUES = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "10",
  "19",
];
export const DEFAULT_FUEL_PRODUCT_CLASS_VALUES = ["27"];
export const DEFAULT_FUEL_SERVICE_TYPE_VALUES = [
  "Benzinas 95",
  "Benzinas 98",
  "DK euro",
];

export type FuelBarcodeDefaultEntry = Pick<FuelBarcode, "type" | "value">;

export const DEFAULT_FUEL_BARCODE_ENTRIES: FuelBarcodeDefaultEntry[] = [
  ...DEFAULT_FUEL_BARCODE_VALUES.map(
    (value): FuelBarcodeDefaultEntry => ({ type: "barcode", value })
  ),
  ...DEFAULT_FUEL_PRODUCT_CLASS_VALUES.map(
    (value): FuelBarcodeDefaultEntry => ({ type: "productClass", value })
  ),
  ...DEFAULT_FUEL_SERVICE_TYPE_VALUES.map(
    (value): FuelBarcodeDefaultEntry => ({ type: "serviceType", value })
  ),
];

/**
 * Same normalization the backend uses:
 *   - barcode: strip leading zeros
 *   - productClass: trim only (case-sensitive, V1/V2 unchanged)
 *   - serviceType: trim + lowercase (case-insensitive, V3)
 */
export function normalizeBarcodeValue(value: string): string {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return "";
  const stripped = trimmed.replace(/^0+/, "");
  return stripped === "" ? "0" : stripped;
}

export function normalizeProductClassValue(value: string): string {
  return (value ?? "").trim();
}

export function normalizeServiceTypeValue(value: string): string {
  return (value ?? "").trim().toLowerCase();
}

/** @deprecated use normalizeServiceTypeValue */
export function normalizeTextValue(value: string): string {
  return normalizeServiceTypeValue(value);
}

/** Stable dedup key matching the backend uniqueness rule (type + normalized value). */
export function fuelBarcodeKey(
  entry: Pick<FuelBarcode, "type" | "value">
): string {
  let value: string;
  if (entry.type === "barcode") {
    value = normalizeBarcodeValue(entry.value);
  } else if (entry.type === "serviceType") {
    value = normalizeServiceTypeValue(entry.value ?? "");
  } else {
    value = normalizeProductClassValue(entry.value ?? "");
  }
  return `${entry.type}:${value}`;
}

const TYPE_ORDER: Record<string, number> = {
  barcode: 0,
  productClass: 1,
  serviceType: 2,
};

/**
 * Ordering for the management grid: barcode → productClass → serviceType;
 * within each group, sort by numeric value (so 2 comes before 10), falling
 * back to string compare for non-numeric values.
 */
export function compareFuelBarcodes(
  a: Pick<FuelBarcode, "type" | "value">,
  b: Pick<FuelBarcode, "type" | "value">
): number {
  const ao = TYPE_ORDER[a.type] ?? 99;
  const bo = TYPE_ORDER[b.type] ?? 99;
  if (ao !== bo) return ao - bo;

  const av = (a.value ?? "").trim();
  const bv = (b.value ?? "").trim();
  const an = Number(av);
  const bn = Number(bv);
  const aNum = av !== "" && !Number.isNaN(an);
  const bNum = bv !== "" && !Number.isNaN(bn);

  if (aNum && bNum) return an - bn;
  if (aNum) return -1;
  if (bNum) return 1;
  return av.localeCompare(bv);
}

export function sortFuelBarcodes<T extends Pick<FuelBarcode, "type" | "value">>(
  list: T[]
): T[] {
  return [...list].sort(compareFuelBarcodes);
}
